import { useEffect, useMemo, useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import { useHistory, useLocation } from "react-router-dom";

import {
  useGetRallyById,
  useGetRallyStages,
  useGetRallies,
} from "../../../../api/jeep-rally/rally/rally.hooks";
import { useGetRankingsByEvent } from "../../../../api/jeep-rally/rankings/rankings.hooks";
import { useGetAllCategories } from "../../../../api/jeep-rally/categories/categories.hooks";
import {
  isJeepRallyAdmin,
  normalizeCategories,
} from "../Categories/utils/category.utils";
import {
  getCategorySelectValue,
  getRallyCategories,
  normalizeRallies,
  normalizeRally,
} from "../Rallies/utils/rally.utils";
import {
  getRankingApiErrorMessage,
  normalizeRankings,
  normalizeStages,
} from "./utils/ranking.utils";
import RankingsFilters from "./List/_components/RankingsFilters";
import RankingsTable from "./List/_components/RankingsTable";

const RankingListPage = () => {
  const history = useHistory();
  const location = useLocation();
  const canManage = isJeepRallyAdmin();

  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const [selectedRallyId, setSelectedRallyId] = useState(
    () => query.get("eventId") || ""
  );
  const [selectedCategory, setSelectedCategory] = useState(
    () => query.get("category") || ""
  );
  const [selectedStage, setSelectedStage] = useState(
    () => query.get("after_stage") || ""
  );

  useEffect(() => {
    const eventId = query.get("eventId") || "";
    const category = query.get("category") || "";
    const afterStage = query.get("after_stage") || "";
    if (eventId) setSelectedRallyId(eventId);
    if (category) setSelectedCategory(category);
    if (afterStage !== "") setSelectedStage(afterStage);
  }, [query]);

  const {
    data: ralliesData,
    isLoading: isRalliesLoading,
    isError: isRalliesError,
    error: ralliesError,
  } = useGetRallies();

  const rallies = useMemo(
    () => normalizeRallies(ralliesData),
    [ralliesData]
  );

  useEffect(() => {
    if (selectedRallyId || !rallies.length) return;
    const firstId = rallies[0]?._id || rallies[0]?.id || "";
    if (firstId) setSelectedRallyId(firstId);
  }, [rallies, selectedRallyId]);

  const {
    data: rallyData,
    isLoading: isRallyLoading,
  } = useGetRallyById(selectedRallyId);

  const { data: allCategoriesData, isLoading: isAllCategoriesLoading } =
    useGetAllCategories();

  const allCategories = useMemo(
    () => normalizeCategories(allCategoriesData),
    [allCategoriesData]
  );

  const rally = useMemo(
    () => normalizeRally(rallyData),
    [rallyData]
  );

  const categories = useMemo(() => {
    const rallyCats = getRallyCategories(rally);
    if (!rallyCats.length) return [];

    return rallyCats
      .map((category) => {
        if (category && typeof category === "object" && category.key) {
          return category;
        }
        const key =
          typeof category === "string"
            ? category
            : getCategorySelectValue(category);
        const match = allCategories.find(
          (item) =>
            item.key === key || item._id === key || item.id === key
        );
        return match || { key, title: key };
      })
      .filter((category) => category?.key);
  }, [rally, allCategories]);

  const {
    data: stagesData,
    isLoading: isStagesLoading,
  } = useGetRallyStages(selectedRallyId, {
    enabled: Boolean(selectedRallyId),
  });

  const stages = useMemo(
    () => normalizeStages(stagesData),
    [stagesData]
  );

  const rankingParams = useMemo(() => {
    const params = { categoryId: selectedCategory };
    if (selectedStage !== "") {
      params.after_stage = Number(selectedStage);
    }
    return params;
  }, [selectedCategory, selectedStage]);

  const {
    data: rankingsData,
    isLoading: isRankingsLoading,
    isFetching: isRankingsFetching,
    isError: isRankingsError,
    error: rankingsError,
  } = useGetRankingsByEvent(selectedRallyId, rankingParams, {
    enabled: Boolean(selectedRallyId) && Boolean(selectedCategory),
  });

  const rankings = useMemo(
    () => normalizeRankings(rankingsData),
    [rankingsData]
  );

  const handleRallyChange = (rallyId) => {
    setSelectedRallyId(rallyId);
    setSelectedCategory("");
    setSelectedStage("");
  };

  const handleAddRankings = () => {
    if (!selectedRallyId || !canManage) return;
    const search = new URLSearchParams({ eventId: selectedRallyId });
    if (selectedCategory) search.set("category", selectedCategory);
    if (selectedStage !== "") search.set("after_stage", selectedStage);
    history.push(`/jeep-rally/ranking/create?${search.toString()}`);
  };

  if (isRalliesError) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body text-danger py-4">
            Failed to load rallies.{" "}
            {getRankingApiErrorMessage(ralliesError, "Please try again.")}
          </div>
        </div>
      </div>
    );
  }

  const showRankingsLoading =
    Boolean(selectedRallyId) &&
    Boolean(selectedCategory) &&
    (isRankingsLoading || isRankingsFetching);

  return (
    <div className="container-fluid">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4 className="card-title mb-0">
            Rankings
            {selectedCategory ? ` (${rankings.length})` : ""}
          </h4>
        </div>

        <div className="card-body">
          <RankingsFilters
            rallies={rallies}
            isRalliesLoading={isRalliesLoading}
            selectedRallyId={selectedRallyId}
            onRallyChange={handleRallyChange}
            categories={categories}
            isCategoriesLoading={
              Boolean(selectedRallyId) &&
              (isRallyLoading || isAllCategoriesLoading)
            }
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
            stages={stages}
            isStagesLoading={Boolean(selectedRallyId) && isStagesLoading}
            selectedStage={selectedStage}
            onStageChange={setSelectedStage}
            canManage={canManage}
            onAddRankings={handleAddRankings}
            addDisabled={!selectedRallyId}
          />

          {!canManage && (
            <Alert variant="warning" className="mb-3">
              Admin access is required to add rankings.
            </Alert>
          )}

          {!selectedRallyId ? (
            <div className="text-center text-muted py-5">
              <p className="mb-0">Select a rally to view rankings.</p>
            </div>
          ) : !selectedCategory ? (
            <div className="text-center text-muted py-5">
              <p className="mb-0">
                Select a category to load rankings for this rally.
              </p>
            </div>
          ) : showRankingsLoading ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          ) : isRankingsError ? (
            <div className="text-danger py-4">
              Failed to load rankings.{" "}
              {getRankingApiErrorMessage(
                rankingsError,
                "Please try again."
              )}
            </div>
          ) : rankings.length === 0 ? (
            <div className="text-center text-muted py-5">
              <p className="mb-0">
                No rankings found for this rally, category
                {selectedStage !== "" ? ", and stage" : ""}.
              </p>
            </div>
          ) : (
            <RankingsTable
              rankings={rankings}
              categories={categories.length ? categories : allCategories}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default RankingListPage;
